import { Box, Button, Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { allAntibiotics, OptionBase, OptionForTargets } from '@resistapp/client/components/overview-focus-header/types';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { ProcessStepSummary } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { friendlyMetricValue, getMetricAndLevel, getMetricColor } from '@resistapp/client/utils/metric-utils';
import { getOverviewConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import { L2Target } from '@resistapp/common/assays';
import { SampleTypeGroup } from '@resistapp/common/comparable-sample-groups';
import { SampleSubType } from '@resistapp/common/sampling-point-types';
import { MetricMode, Process, ProcessFlow, ProcessMode } from '@resistapp/common/types';
import { chain } from 'lodash';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ScopeSelectors } from '../overview-focus-header/scope-selectors';
import { theme } from '../shared/theme';
import { getEffectiveStepProcessModeForComplexProcess } from './process-step-mode';

const PROCESS_TABLE_PAGE_SIZE = 8;
const SELECTED_ROW_HEIGHT_PX = 66;
const DEFAULT_ROW_HEIGHT_PX = 52;
const UNSELECTED_ROW_HEIGHT_WITH_SELECTION_PX = 50;
const FIXED_TABLE_BODY_HEIGHT_PX =
  SELECTED_ROW_HEIGHT_PX + (PROCESS_TABLE_PAGE_SIZE - 1) * UNSELECTED_ROW_HEIGHT_WITH_SELECTION_PX;
const standardStepKeys = ['R', 'T', 'F', 'C', 'W', '1', '2', '3'] as const;
const shrimpStepKeys = ['1', '2', '3'] as const;

type StandardStepKey = (typeof standardStepKeys)[number];
type ShrimpStepKey = (typeof shrimpStepKeys)[number];

type FlowStepKey = Extract<StandardStepKey, 'R' | 'T' | 'F' | 'C' | 'W'>;

type ColumnDef =
  | { type: 'site' }
  | { type: 'step'; key: FlowStepKey; header: ReactNode; w: string }
  | { type: 'marker'; marker: '>' | '+'; id: string; leftKey?: FlowStepKey; rightKey?: FlowStepKey }
  | { type: 'shrimps'; w: string };

const STEP_COL_W = '90px';
const SHRIMPS_COL_W = '130px';
const SITE_COL_W = '220px';

const tableColumns: ColumnDef[] = [
  { type: 'site' },
  { type: 'step', key: 'R', header: 'River', w: STEP_COL_W },
  { type: 'marker', marker: '>', id: 'R-T' },
  {
    type: 'step',
    key: 'T',
    header: (
      <>
        Disinfected <br />
        inlet water
      </>
    ),
    w: STEP_COL_W,
  },
  { type: 'marker', marker: '+', id: 'T-F', leftKey: 'T', rightKey: 'F' },
  { type: 'step', key: 'F', header: 'Feed', w: STEP_COL_W },
  { type: 'marker', marker: '>', id: 'F-C' },
  {
    type: 'step',
    key: 'C',
    header: (
      <>
        Culture
        <br />
        pond water
      </>
    ),
    w: STEP_COL_W,
  },
  { type: 'marker', marker: '>', id: 'C-W' },
  { type: 'step', key: 'W', header: 'Pond WW', w: STEP_COL_W },
  { type: 'marker', marker: '+', id: 'W-S', leftKey: 'W' },
  { type: 'shrimps', w: SHRIMPS_COL_W },
];

interface ProcessSiteRow {
  processId: number;
  siteLabel: string;
  stepMetricsByKey: Partial<Record<StandardStepKey, ProcessStepMetric>>;
  stepPointIdsByKey: Partial<Record<StandardStepKey, number>>;
}

interface ProcessStepMetric {
  value: number | null;
  label: string;
  backgroundColor: string;
  textColor: string;
}

interface ProcessStepSelection {
  processId: number;
  stepKey: StandardStepKey;
  stepPointId: number;
}

interface ShrimpMetricPill {
  key: StandardStepKey;
  value: ProcessStepMetric | undefined;
  onClick: (() => void) | undefined;
  selected: boolean;
  dimmed: boolean;
}

export function OverviewProcessSitesTable() {
  const { data, queryFilters } = useSampleDataContext();
  const { getGroup, allAssays } = useAssayContext();
  const {
    processGroupMapData,
    selectedProcessId,
    selectedStepPointId,
    selectedFlowId,
    selectedCountry,
    metricMode,
    defaultProcessMode,
    activeAbundanceMode,
    setProcessSelectionStable,
    setShownAdminLevelStable,
  } = useOverviewContext();
  const selectedSamplingPointIdsOrdered = queryFilters.filters.selectedSamplingPointIdsOrdered;
  const { toggleSamplingPointStable } = queryFilters;

  const [page, setPage] = useState(1);
  const [sampleTypeSelectorOpen, setSampleTypeSelectorOpen] = useState(false);
  const processDatums = useMemo(
    () =>
      (processGroupMapData ?? []).filter(
        datum => datum.samplingPoint.country === selectedCountry || !selectedCountry,
      ),
    [processGroupMapData, selectedCountry],
  );

  const rows = useMemo<ProcessSiteRow[]>(() => {
    const overviewConfiguration = getOverviewConfiguration(metricMode, activeAbundanceMode);
    return chain(processDatums)
      .map(datum => {
        if (!datum.processId) {
          return undefined;
        }

        const stepMetricsByKey: Partial<Record<StandardStepKey, ProcessStepMetric>> = {};
        const stepPointIdsByKey: Partial<Record<StandardStepKey, number>> = {};
        const shrimpStepKeyBySamplingPointId = getShrimpStepKeyBySamplingPointId(datum.processStepSummaries ?? []);

        (datum.processStepSummaries ?? []).forEach(stepSummary => {
          const stepKey = getProcessStepKey(stepSummary, stepMetricsByKey, shrimpStepKeyBySamplingPointId);
          if (!stepKey || stepMetricsByKey[stepKey]) {
            return;
          }
          const effectiveStepProcessMode: ProcessMode = getEffectiveStepProcessModeForComplexProcess(
            stepSummary,
            defaultProcessMode,
          );
          const [stepMetric] = getMetricAndLevel(
            {
              beforeAbundances: stepSummary.beforeAbundances,
              afterAbundances: stepSummary.afterAbundances,
            },
            queryFilters.filters.selectedTargets,
            metricMode,
            effectiveStepProcessMode,
            activeAbundanceMode,
            getGroup,
            allAssays,
            true,
          );
          stepMetricsByKey[stepKey] = {
            value: stepMetric,
            label: friendlyMetricValue(stepMetric, metricMode, activeAbundanceMode),
            backgroundColor: getMetricColor(stepMetric, metricMode, activeAbundanceMode),
            textColor: overviewConfiguration.getFriendlyTextColor(stepMetric),
          };
          stepPointIdsByKey[stepKey] = stepSummary.samplingPointId;
        });

        return {
          processId: datum.processId,
          siteLabel: getProcessSiteLabel(datum.processStepSummaries),
          stepMetricsByKey,
          stepPointIdsByKey,
        };
      })
      .compact()
      .sortBy(row => row.siteLabel.toLowerCase())
      .value();
  }, [
    processDatums,
    queryFilters.filters.selectedTargets,
    metricMode,
    defaultProcessMode,
    activeAbundanceMode,
    getGroup,
    allAssays,
  ]);

  const selectedStepSelection = useMemo(
    () => findSelectionByStepPointId(rows, selectedStepPointId),
    [rows, selectedStepPointId],
  );

  const querySelectedStepSelection = useMemo(() => {
    if (selectedSamplingPointIdsOrdered.length > 2) {
      return undefined;
    }

    return selectedSamplingPointIdsOrdered
      .map(samplingPointId => findSelectionByStepPointId(rows, samplingPointId))
      .find((selection): selection is ProcessStepSelection => !!selection);
  }, [rows, selectedSamplingPointIdsOrdered]);

  const activeStepSelection = selectedStepSelection ?? querySelectedStepSelection;
  const activeProcessId = activeStepSelection?.processId;
  const activeStepKey = activeStepSelection?.stepKey;
  const hasActiveStepSelection = !!activeStepSelection;

  const applyStepSelectionStable = useCallback(
    (
      stepSelection: ProcessStepSelection,
      options?: {
        syncSamplingPointFilter?: boolean;
      },
    ) => {
      const process = data?.processes?.find(processLocal => processLocal.id === stepSelection.processId);
      if (!process) {
        return;
      }
      const selectedFlow = getFlowForStep(process, stepSelection.stepPointId);
      const selectedSamplingPointIds = getSelectedSamplingPointIds(metricMode, stepSelection.stepPointId, selectedFlow);

      setProcessSelectionStable({
        selectedProcessId: stepSelection.processId,
        selectedStepPointId: stepSelection.stepPointId,
        selectedFlowId: selectedFlow?.id,
        comparedStepPointId: undefined,
      });
      setShownAdminLevelStable(null);

      if (options?.syncSamplingPointFilter === false) {
        return;
      }

      toggleSamplingPointStable(
        selectedSamplingPointIds.length === 1 ? selectedSamplingPointIds[0] : selectedSamplingPointIds,
        true,
      );
    },
    [data?.processes, metricMode, setProcessSelectionStable, setShownAdminLevelStable, toggleSamplingPointStable],
  );

  useEffect(() => {
    if (!rows.length) {
      return;
    }

    if (!activeStepSelection) {
      const hasAnyProcessSelection = !!selectedProcessId || !!selectedStepPointId || !!selectedFlowId;
      if (hasAnyProcessSelection) {
        setProcessSelectionStable({});
      }
      return;
    }

    const process = data?.processes?.find(processLocal => processLocal.id === activeStepSelection.processId);
    if (!process) {
      return;
    }

    const selectedFlow = getFlowForStep(process, activeStepSelection.stepPointId);
    const expectedSamplingPointIds = getSelectedSamplingPointIds(
      metricMode,
      activeStepSelection.stepPointId,
      selectedFlow,
    );

    const shouldSyncProcessSelection =
      selectedProcessId !== activeStepSelection.processId ||
      selectedStepPointId !== activeStepSelection.stepPointId ||
      selectedFlowId !== selectedFlow?.id;

    if (shouldSyncProcessSelection) {
      applyStepSelectionStable(activeStepSelection, {
        syncSamplingPointFilter: false,
      });
    }

    if (!areNumberArraysEqual(selectedSamplingPointIdsOrdered, expectedSamplingPointIds)) {
      toggleSamplingPointStable(
        expectedSamplingPointIds.length === 1 ? expectedSamplingPointIds[0] : expectedSamplingPointIds,
        true,
      );
    }
  }, [
    rows,
    activeStepSelection,
    activeStepKey,
    selectedProcessId,
    selectedStepPointId,
    selectedFlowId,
    data?.processes,
    metricMode,
    selectedSamplingPointIdsOrdered,
    toggleSamplingPointStable,
    applyStepSelectionStable,
    setProcessSelectionStable,
  ]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PROCESS_TABLE_PAGE_SIZE));
  useEffect(() => {
    setPage(previous => Math.min(previous, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!activeProcessId) {
      return;
    }
    const selectedIndex = rows.findIndex(row => row.processId === activeProcessId);
    if (selectedIndex < 0) {
      return;
    }
    setPage(Math.floor(selectedIndex / PROCESS_TABLE_PAGE_SIZE) + 1);
  }, [rows, activeProcessId]);

  const pagedRows = useMemo(() => {
    const offset = (page - 1) * PROCESS_TABLE_PAGE_SIZE;
    return rows.slice(offset, offset + PROCESS_TABLE_PAGE_SIZE);
  }, [rows, page]);

  const hasSelectedProcess = useMemo(
    () => !!activeProcessId && rows.some(row => row.processId === activeProcessId),
    [rows, activeProcessId],
  );

  const occupiedTableBodyHeightPx = useMemo(
    () =>
      pagedRows.reduce((sum, row) => {
        const selected = row.processId === activeProcessId;
        return sum + getProcessTableRowHeightPx(selected, hasSelectedProcess);
      }, 0),
    [pagedRows, activeProcessId, hasSelectedProcess],
  );

  const spacerTableBodyHeightPx = Math.max(0, FIXED_TABLE_BODY_HEIGHT_PX - occupiedTableBodyHeightPx);

  const selectSiteStable = useCallback(
    (row: ProcessSiteRow) => {
      const rowSelection = getFirstStepSelectionForRow(row, activeStepKey ?? undefined);
      if (!rowSelection) {
        return;
      }
      applyStepSelectionStable(rowSelection);
    },
    [activeStepKey, applyStepSelectionStable],
  );
  const clearSiteSelectionStable = useCallback(() => {
    setProcessSelectionStable({});
    toggleSamplingPointStable(undefined, true);
  }, [setProcessSelectionStable, toggleSamplingPointStable]);

  const changePageStable = useCallback(
    (nextPage: number) => {
      const boundedPage = Math.max(1, Math.min(totalPages, nextPage));
      if (boundedPage === page) {
        return;
      }

      if (hasSelectedProcess) {
        clearSiteSelectionStable();
      }

      setPage(boundedPage);
    },
    [totalPages, page, hasSelectedProcess, clearSiteSelectionStable],
  );

  const handleAntibioticChangeStable = (value: OptionForTargets | null) => {
    const correctValue = !value || value.value === allAntibiotics ? queryFilters.filters.selectedTargets : value.value;
    if (correctValue === allAntibiotics || !value) {
      queryFilters.toggleGeneGroupStable(correctValue as L2Target, true);
    } else {
      queryFilters.toggleSingleTarget(value.value);
    }
  };

  const handleSampleTypeGroupChangeStable = (value: OptionBase['value'] | undefined) => {
    const correctValue = !value ? queryFilters.filters.selectedSampleTypeGroup : value;
    queryFilters.setSampleTypeGroupStable(correctValue as SampleTypeGroup, false);
  };

  if (!rows.length) {
    return (
      <Box width="100%" px="36px" py={4}>
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" px={4} py={6}>
          <Text fontSize="sm" color="gray.600">
            No process sites available for the selected filters.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box width="100%" px="36px" py={4}>
      <TableContainer border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" overflowX="auto">
        <Table size="sm" variant="unstyled" width="100%" sx={{ tableLayout: 'fixed' }}>
          <Thead>
            <Tr borderBottom="1px solid" borderColor="gray.200">
              {tableColumns.map(col => {
                if (col.type === 'site') {
                  return <Th key="site" py={2} w={SITE_COL_W} />;
                }
                if (col.type === 'marker') {
                  return <Th key={col.id} px={0} w="20px" />;
                }
                if (col.type === 'shrimps') {
                  return (
                    <Th
                      key="shrimps"
                      textTransform="none"
                      fontSize={theme.fontSize.medium}
                      fontWeight={theme.fontWeight.heavy}
                      color={theme.colors.neutral800}
                      fontFamily={theme.fontFamily.baseVariable}
                      py={2}
                      textAlign="center"
                      verticalAlign="middle"
                      w={col.w}
                    >
                      Shrimps
                    </Th>
                  );
                }
                return (
                  <Th
                    key={col.key}
                    textTransform="none"
                    fontSize={theme.fontSize.medium}
                    fontWeight={theme.fontWeight.heavy}
                    color={theme.colors.neutral800}
                    fontFamily={theme.fontFamily.baseVariable}
                    py={2}
                    textAlign="center"
                    verticalAlign="middle"
                    w={col.w}
                  >
                    {col.header}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {pagedRows.map(row => {
              const selected = row.processId === activeProcessId;
              const rowDimmed = hasActiveStepSelection && !selected;
              const rowHeightPx = getProcessTableRowHeightPx(selected, hasSelectedProcess);
              return (
                <Tr
                  key={row.processId}
                  bg={selected ? 'gray.50' : undefined}
                  _hover={{ bg: selected ? 'gray.100' : 'gray.50' }}
                >
                  {tableColumns.map(col => {
                    if (col.type === 'site') {
                      return (
                        <Td key="site" py={0} h={`${rowHeightPx}px`} verticalAlign="middle" pr={4}>
                          <Button
                            variant="link"
                            color="inherit"
                            cursor="pointer"
                            textDecoration="none"
                            _hover={{ textDecoration: 'none' }}
                            fontWeight={selected ? 'bold' : 'normal'}
                            fontSize={selected ? theme.fontSize.h2 : 'md'}
                            fontFamily={selected ? theme.fontFamily.dmSans : theme.fontFamily.baseVariable}
                            whiteSpace="nowrap"
                            onClick={() => {
                              if (selected) {
                                clearSiteSelectionStable();
                                return;
                              }
                              selectSiteStable(row);
                            }}
                          >
                            {row.siteLabel}
                          </Button>
                        </Td>
                      );
                    }

                    if (col.type === 'marker') {
                      const showMarker = shouldShowMarker(col, row);
                      return (
                        <Td key={col.id} px={0} py={0} h={`${rowHeightPx}px`} verticalAlign="middle" textAlign="center">
                          {showMarker ? <FlowMarkerIcon marker={col.marker} dimmed={rowDimmed} /> : null}
                        </Td>
                      );
                    }

                    if (col.type === 'shrimps') {
                      const pills: ShrimpMetricPill[] = shrimpStepKeys.map(stepKey => {
                        const shrimpStepPointId = row.stepPointIdsByKey[stepKey];
                        const shrimpActiveMetricInSelectedRow = selected && activeStepKey === stepKey;
                        const shrimpDimmed = hasActiveStepSelection && !shrimpActiveMetricInSelectedRow;
                        return {
                          key: stepKey,
                          value: row.stepMetricsByKey[stepKey],
                          onClick: shrimpStepPointId
                            ? () => {
                                if (shrimpActiveMetricInSelectedRow) {
                                  clearSiteSelectionStable();
                                  return;
                                }
                                const shrimpSelection = getSelectionByStepKey(row, stepKey);
                                shrimpSelection && applyStepSelectionStable(shrimpSelection);
                              }
                            : undefined,
                          selected,
                          dimmed: shrimpDimmed,
                        };
                      });

                      return (
                        <Td
                          key="shrimps"
                          py={0}
                          h={`${rowHeightPx}px`}
                          verticalAlign="middle"
                          textAlign="center"
                          w={col.w}
                        >
                          <ShrimpMetricCell pills={pills} />
                        </Td>
                      );
                    }

                    const stepPointId = row.stepPointIdsByKey[col.key];
                    const selection = getSelectionByStepKey(row, col.key);
                    const rowActiveMetricInSelectedRow = selected && activeStepKey === col.key;
                    const metricDimmed = hasActiveStepSelection && !rowActiveMetricInSelectedRow;
                    return (
                      <Td
                        key={col.key}
                        py={0}
                        h={`${rowHeightPx}px`}
                        verticalAlign="middle"
                        textAlign="center"
                        w={col.w}
                      >
                        <Flex justify="center">
                          <MetricPill
                            value={row.stepMetricsByKey[col.key]}
                            selected={selected}
                            dimmed={metricDimmed}
                            onClick={
                              stepPointId && selection
                                ? () => {
                                    if (rowActiveMetricInSelectedRow) {
                                      clearSiteSelectionStable();
                                      return;
                                    }
                                    applyStepSelectionStable(selection);
                                  }
                                : undefined
                            }
                          />
                        </Flex>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
            {spacerTableBodyHeightPx > 0 && (
              <Tr>
                <Td colSpan={tableColumns.length} py={0} h={`${spacerTableBodyHeightPx}px`} />
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Flex
        justify="space-between"
        align={{ base: 'flex-start', lg: 'center' }}
        mt={3}
        px={1}
        gap={theme.spacing[8]}
        flexDirection={{ base: 'column', lg: 'row' }}
      >
        <Box display={{ base: 'none', lg: 'block' }} flex={1} />
        {/*
          Temporarily hidden in process mode:
          - "All samples" selector (sample type)
          - timepoint selector (month)
        <ScopeSelectors
          onChangeAntibiotic={handleAntibioticChangeStable as (value: OptionBase | OptionForTargets | null) => void}
          onChangeSampleTypeGroup={handleSampleTypeGroupChangeStable}
          selectedTargets={queryFilters.filters.selectedTargets}
          envTypeSelectorOpen={sampleTypeSelectorOpen}
          setSampleTypeSelectorOpen={setSampleTypeSelectorOpen}
          layout="row"
          showAntibiotic={false}
        />
        */}
        <Flex align="center" gap={theme.spacing[6]}>
          <ScopeSelectors
            onChangeAntibiotic={handleAntibioticChangeStable as (value: OptionBase | OptionForTargets | null) => void}
            onChangeSampleTypeGroup={handleSampleTypeGroupChangeStable}
            selectedTargets={queryFilters.filters.selectedTargets}
            envTypeSelectorOpen={sampleTypeSelectorOpen}
            setSampleTypeSelectorOpen={setSampleTypeSelectorOpen}
            layout="row"
            showSampleType={false}
            showMonth={false}
          />
          {rows.length > PROCESS_TABLE_PAGE_SIZE && (
            <Flex align="center" gap={3}>
              <Button
                variant="link"
                size="sm"
                fontSize={theme.fontSize.medium}
                color={page <= 1 ? 'gray.300' : 'gray.600'}
                fontWeight={theme.fontWeight.bold}
                fontFamily={theme.fontFamily.baseVariable}
                textDecoration="none"
                isDisabled={page <= 1}
                _hover={{ textDecoration: 'none' }}
                onClick={() => {
                  changePageStable(page - 1);
                }}
                leftIcon={
                  <FontAwesomeIcon icon={faChevronRight} style={{ transform: 'rotate(180deg)', width: '7px' }} />
                }
              >
                Previous
              </Button>
              <Text
                fontSize={theme.fontSize.medium}
                fontWeight={theme.fontWeight.bold}
                fontFamily={theme.fontFamily.baseVariable}
                color="gray.500"
              >
                {page} / {totalPages}
              </Text>
              <Button
                variant="link"
                size="sm"
                fontSize={theme.fontSize.medium}
                color={page >= totalPages ? 'gray.300' : 'gray.600'}
                fontWeight={theme.fontWeight.bold}
                fontFamily={theme.fontFamily.baseVariable}
                textDecoration="none"
                isDisabled={page >= totalPages}
                _hover={{ textDecoration: 'none' }}
                onClick={() => {
                  changePageStable(page + 1);
                }}
                rightIcon={<FontAwesomeIcon icon={faChevronRight} style={{ width: '7px' }} />}
              >
                Next
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

function getProcessSiteLabel(stepSummaries: ProcessStepSummary[] | undefined): string {
  const prefixes = chain(stepSummaries ?? [])
    .map(stepSummary => getNamePrefix(stepSummary.name))
    .compact()
    .uniq()
    .value();
  if (prefixes.length === 1) {
    return prefixes[0];
  }
  return stepSummaries?.[0]?.name ?? 'Process site';
}

function getProcessTableRowHeightPx(selected: boolean, hasSelectedProcess: boolean): number {
  if (selected) {
    return SELECTED_ROW_HEIGHT_PX;
  }
  return hasSelectedProcess ? UNSELECTED_ROW_HEIGHT_WITH_SELECTION_PX : DEFAULT_ROW_HEIGHT_PX;
}

function shouldShowMarker(col: Extract<ColumnDef, { type: 'marker' }>, row: ProcessSiteRow): boolean {
  if (col.marker === '>') {
    return true;
  }
  const hasLeft = col.leftKey ? !!row.stepMetricsByKey[col.leftKey] : true;
  const hasRight = col.rightKey
    ? !!row.stepMetricsByKey[col.rightKey]
    : shrimpStepKeys.some(key => !!row.stepMetricsByKey[key]);
  return hasLeft && hasRight;
}

function ShrimpMetricCell({ pills }: { pills: ShrimpMetricPill[] }) {
  const hasAnyValue = pills.some(pill => !!pill.value);
  if (!hasAnyValue) {
    return null;
  }

  return (
    <Flex align="center" justify="center" gap={1}>
      {pills.map(pill =>
        pill.value ? (
          <MetricPill
            key={pill.key}
            value={pill.value}
            compact
            selected={pill.selected}
            dimmed={pill.dimmed}
            squeezeHorizontally={pill.selected}
            onClick={pill.onClick}
          />
        ) : (
          <Box key={`${pill.key}-empty`} width={pill.selected ? '36px' : '32px'} />
        ),
      )}
    </Flex>
  );
}

function FlowMarkerIcon({ marker, dimmed = false }: { marker: '>' | '+'; dimmed?: boolean }) {
  return (
    <FontAwesomeIcon
      icon={marker === '>' ? faChevronRight : faPlus}
      style={{ color: theme.colors.neutral400, width: '14px', opacity: dimmed ? 0.5 : 1 }}
    />
  );
}

function MetricPill({
  value,
  compact = false,
  selected = false,
  dimmed = false,
  squeezeHorizontally = false,
  onClick,
}: {
  value: ProcessStepMetric | undefined;
  compact?: boolean;
  selected?: boolean;
  dimmed?: boolean;
  squeezeHorizontally?: boolean;
  onClick?: () => void;
}) {
  if (!value) {
    return null;
  }

  const effectiveDimmed = dimmed;
  const isMissingMetric = value.value === null;
  const minWidth = selected ? (squeezeHorizontally ? '36px' : '88px') : compact ? '32px' : '48px';
  const height = selected ? '40px' : compact ? '28px' : '32px';
  const fontSize = selected ? (squeezeHorizontally ? 'sm' : theme.fontSize.h3) : compact ? 'xs' : 'sm';
  const paddingX = selected ? (squeezeHorizontally ? 1 : 2) : compact ? 1 : 2;

  return (
    <Flex
      as={onClick ? 'button' : undefined}
      onClick={onClick}
      cursor={onClick ? 'pointer' : undefined}
      align="center"
      justify="center"
      minW={minWidth}
      maxW={squeezeHorizontally ? minWidth : undefined}
      h={height}
      px={paddingX}
      borderRadius="6px"
      border={isMissingMetric ? '1px solid' : 'none'}
      borderColor={isMissingMetric ? 'gray.300' : undefined}
      bg={isMissingMetric ? 'white' : value.backgroundColor}
      color={value.textColor}
      fontSize={fontSize}
      fontWeight={selected ? theme.fontWeight.bold : '600'}
      lineHeight="1"
      opacity={effectiveDimmed ? 0.45 : 1}
      _hover={onClick ? { opacity: effectiveDimmed ? 0.6 : 0.85 } : undefined}
      transition="opacity 0.15s"
    >
      {value.label}
    </Flex>
  );
}

function getProcessStepKey(
  stepSummary: ProcessStepSummary,
  stepMetricsByKey: Partial<Record<StandardStepKey, ProcessStepMetric>>,
  shrimpStepKeyBySamplingPointId: Record<number, ShrimpStepKey | undefined>,
): StandardStepKey | undefined {
  const subtypeKey = getStepKeyBySubtype(stepSummary.subtype);
  if (subtypeKey && !stepMetricsByKey[subtypeKey]) {
    return subtypeKey;
  }

  if (stepSummary.subtype === SampleSubType.SHRIMP) {
    const shrimpKey = shrimpStepKeyBySamplingPointId[stepSummary.samplingPointId];
    if (shrimpKey && !stepMetricsByKey[shrimpKey]) {
      return shrimpKey;
    }
    return shrimpStepKeys.find(stepKey => !stepMetricsByKey[stepKey]);
  }

  const fallbackKey = getStepKeyFromName(stepSummary.name);
  if (!fallbackKey || stepMetricsByKey[fallbackKey]) {
    return undefined;
  }
  return fallbackKey;
}

function getShrimpStepKeyBySamplingPointId(
  stepSummaries: ProcessStepSummary[],
): Record<number, ShrimpStepKey | undefined> {
  return chain(stepSummaries)
    .filter(stepSummary => stepSummary.subtype === SampleSubType.SHRIMP)
    .sortBy(stepSummary => stepSummary.samplingPointId)
    .sortBy(stepSummary => stepSummary.name.toLowerCase())
    .slice(0, shrimpStepKeys.length)
    .map((stepSummary, index) => ({
      samplingPointId: stepSummary.samplingPointId,
      stepKey: shrimpStepKeys[index],
    }))
    .keyBy(entry => entry.samplingPointId)
    .mapValues(entry => entry.stepKey)
    .value();
}

function getStepKeyBySubtype(subtype: ProcessStepSummary['subtype']): FlowStepKey | undefined {
  switch (subtype) {
    case SampleSubType.FRESH_RIVER_WATER_UPSTREAM:
      return 'R';
    case SampleSubType.CHEMICALLY_DISINFECTED_WATER:
      return 'T';
    case SampleSubType.ANIMAL_FEED:
      return 'F';
    case SampleSubType.SHRIMP_CULTURE_WATER:
      return 'C';
    case SampleSubType.AQUACULTURE_WASTEWATER_RAW:
      return 'W';
    default:
      return undefined;
  }
}

function getStepKeyFromName(name: string): StandardStepKey | undefined {
  const stepKeyRaw = (getNameSuffix(name) || name).trim().toUpperCase();
  const canonicalStepKey = stepKeyRaw === 'D' ? 'W' : stepKeyRaw;
  if (!isStandardStepKey(canonicalStepKey)) {
    return undefined;
  }
  return canonicalStepKey;
}

function isStandardStepKey(stepKey: string): stepKey is StandardStepKey {
  return (standardStepKeys as readonly string[]).includes(stepKey);
}

function findSelectionByStepPointId(
  rows: ProcessSiteRow[],
  stepPointId: number | undefined,
): ProcessStepSelection | undefined {
  if (!stepPointId) {
    return undefined;
  }

  return chain(rows)
    .flatMap(row =>
      standardStepKeys.map(stepKey => ({
        processId: row.processId,
        stepKey,
        stepPointId: row.stepPointIdsByKey[stepKey],
      })),
    )
    .find(
      (
        selection,
      ): selection is {
        processId: number;
        stepKey: StandardStepKey;
        stepPointId: number;
      } => selection.stepPointId === stepPointId,
    )
    .value();
}

function getSelectionByStepKey(row: ProcessSiteRow, stepKey: StandardStepKey): ProcessStepSelection | undefined {
  const stepPointId = row.stepPointIdsByKey[stepKey];
  if (!stepPointId) {
    return undefined;
  }

  return {
    processId: row.processId,
    stepKey,
    stepPointId,
  };
}

function getFirstStepSelectionForRow(
  row: ProcessSiteRow,
  preferredStepKey: StandardStepKey | undefined,
): ProcessStepSelection | undefined {
  const orderedStepKeys = preferredStepKey
    ? [preferredStepKey, ...standardStepKeys.filter(stepKey => stepKey !== preferredStepKey)]
    : [...standardStepKeys];

  return chain(orderedStepKeys)
    .map(stepKey => getSelectionByStepKey(row, stepKey))
    .find((selection): selection is ProcessStepSelection => !!selection)
    .value();
}

function getFlowForStep(process: Process, stepPointId: number): ProcessFlow | undefined {
  const sortedFlows = getSortedProcessFlows(process.flows ?? []);
  const matchingFlows = sortedFlows
    .filter(flow => flow.fromPointId === stepPointId || flow.toPointId === stepPointId)
    .sort((left, right) => compareMatchingFlowsForStep(left, right, stepPointId));
  return matchingFlows[0] ?? sortedFlows[0];
}

function getSortedProcessFlows(flows: ProcessFlow[]): ProcessFlow[] {
  return [...flows].sort(compareProcessFlowStable);
}

function compareMatchingFlowsForStep(left: ProcessFlow, right: ProcessFlow, stepPointId: number): number {
  const directionDiff = getFlowDirectionOrder(left, stepPointId) - getFlowDirectionOrder(right, stepPointId);
  if (directionDiff !== 0) {
    return directionDiff;
  }
  return compareProcessFlowStable(left, right);
}

function getFlowDirectionOrder(flow: ProcessFlow, stepPointId: number): number {
  return flow.fromPointId === stepPointId ? 0 : 1;
}

function compareProcessFlowStable(left: ProcessFlow, right: ProcessFlow): number {
  return (
    left.processId - right.processId ||
    left.fromPointId - right.fromPointId ||
    left.toPointId - right.toPointId ||
    left.id - right.id
  );
}

function getSelectedSamplingPointIds(
  metricMode: MetricMode,
  stepPointId: number,
  selectedFlow: ProcessFlow | undefined,
): number[] {
  if (metricMode === MetricMode.REDUCTION && selectedFlow) {
    return [selectedFlow.fromPointId, selectedFlow.toPointId];
  }
  return [stepPointId];
}

function areNumberArraysEqual(arrA: number[], arrB: number[]): boolean {
  if (arrA.length !== arrB.length) {
    return false;
  }

  return arrA.every((value, index) => value === arrB[index]);
}

function getNamePrefix(name: string): string | undefined {
  const trimmedName = name.trim();
  const splitIndex = trimmedName.lastIndexOf('-');
  if (splitIndex <= 0) {
    return undefined;
  }
  return trimmedName.slice(0, splitIndex);
}

function getNameSuffix(name: string): string | undefined {
  const trimmedName = name.trim();
  const splitIndex = trimmedName.lastIndexOf('-');
  if (splitIndex < 0 || splitIndex === trimmedName.length - 1) {
    return undefined;
  }
  return trimmedName
    .slice(splitIndex + 1)
    .trim()
    .toUpperCase();
}
